import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import RadioButtonChecked from "@mui/icons-material/RadioButtonChecked";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import { Issue, SubmissionStepType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { stepperStepPath } from "hooks/useStepperNavigation";
import { SubmissionStepState, useSubmissionIssuesGroupedByStep } from "hooks/useSubmissionSteps";
import { useWasteStepper } from "hooks/useWasteStepper";
import pluralize from "pluralize";
import { useNavigate, useParams } from "react-router-dom";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

import { DetailsCard } from "./DetailsCard";
import { FilingProgress } from "./FilingProgress";
import { FrozenDetailsPanel } from "./FrozenDetailsPanel";
import { MarkFilingCompleteAction } from "./MarkFilingCompleteAction";
import { StepHeader } from "./StepHeader";
import { useSubmissionWasteActivityIssues } from "./useSubmissionWasteActivityIssues";

const MAX_LISTED_ISSUES = 10;

/** Status chip on each section card — same outlined StatusChip as the sidebar. */
function SectionStatusChip({
  status,
  issueCount,
}: {
  status?: SubmissionStepState;
  issueCount: number;
}) {
  if (!status) {
    return null;
  }
  const config: Record<
    SubmissionStepState,
    { text: string; color: StatusChipColor; icon: React.ReactElement }
  > = {
    [SubmissionStepState.NotStarted]: {
      text: "Not Started",
      color: "default",
      icon: <PlayCircleOutline />,
    },
    [SubmissionStepState.InProgress]: {
      text: "In Progress",
      color: "info",
      icon: <RadioButtonChecked />,
    },
    [SubmissionStepState.Done]: {
      text: "Complete",
      color: "success",
      icon: <CheckCircleOutlineRounded />,
    },
    [SubmissionStepState.Invalid]: {
      text: `Fix ${issueCount} ${pluralize("Issue", issueCount)}`,
      color: "error",
      icon: <ErrorOutlineRounded />,
    },
  };
  const { text, color, icon } = config[status];
  return (
    <StatusChip
      color={color}
      variant="outlined"
      label={text}
      icon={icon}
      sx={{ flexShrink: 0, "& .MuiChip-icon": { color: "inherit" } }}
    />
  );
}

/**
 * A single section card on the overview: section name, its source description,
 * a red bulleted list of that section's issues (capped at 10, with an "N more"
 * tail), and a status pill. Clicking the card opens the step. Mirrors the Tier
 * II report's OverviewCard.
 */
function SectionCard({
  step,
  status,
  issues,
  onOpen,
}: {
  step: SubmissionStepType;
  status?: SubmissionStepState;
  issues: Issue[];
  onOpen: () => void;
}) {
  const theme = useTheme();
  const { isStaff } = useCurrentUser();
  const presentation = WASTE_SUBMISSION_STEP_PRESENTATION[step];
  const description =
    !isStaff && presentation.customerSourceDescription
      ? presentation.customerSourceDescription
      : presentation.sourceDescription;
  const listed = issues.slice(0, MAX_LISTED_ISSUES);
  const moreCount = issues.length - listed.length;

  return (
    <Paper
      sx={{
        p: 2,
        "&:hover": { backgroundColor: theme.palette.action.hover, cursor: "pointer" },
      }}
      onClick={onOpen}
    >
      <Box display="flex" flexDirection="row" justifyContent="space-between" gap={4}>
        <Stack spacing={1}>
          <Typography variant="body1" fontWeight="medium">
            {presentation.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          {issues.length > 0 && (
            <Box>
              <Typography variant="body2" fontWeight="bold" color="error.main" sx={{ mb: 0.5 }}>
                Issues:
              </Typography>
              <List sx={{ listStyleType: "disc", pt: 0, pl: 2, color: theme.palette.error.main }}>
                {listed.map((issue, index) => (
                  <ListItem key={index} sx={{ m: 0, p: 0, display: "list-item" }}>
                    <ListItemText
                      secondary={issue.message}
                      secondaryTypographyProps={{ color: theme.palette.error.main }}
                    />
                  </ListItem>
                ))}
                {moreCount > 0 && (
                  <ListItem sx={{ m: 0, p: 0, display: "list-item" }}>
                    <ListItemText
                      secondary={`${moreCount} more ${pluralize("issue", moreCount)}`}
                      secondaryTypographyProps={{ color: theme.palette.error.main }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </Stack>
        <SectionStatusChip status={status} issueCount={issues.length} />
      </Box>
    </Paper>
  );
}

/**
 * Stepper landing page, modeled on the Tier II report Overview.
 *
 * While the submission is editable/in-flight it lists each section as a card —
 * name, description, the section's issues, and a status pill — and clicking a
 * card opens that step. Once the submission is frozen (submitted/filed) it shows
 * the read-only "Filing Progress" view instead (matching the Tier II submitted
 * experience).
 */
export function StepperOverview() {
  const { tenantId = "", submissionId = "" } = useParams<{
    tenantId: string;
    submissionId: string;
  }>();
  const navigate = useNavigate();
  const { submission, facilityId, stepStates, isFrozen } = useWasteStepper();
  const wasteActivityIssues = useSubmissionWasteActivityIssues(
    submissionId,
    submission?.wasteActivity ?? null
  );
  const issuesByStep = useSubmissionIssuesGroupedByStep(
    facilityId,
    wasteActivityIssues,
    submissionId
  );

  if (isFrozen) {
    return (
      <Stack spacing={2}>
        <FilingProgress />
        <FrozenDetailsPanel submissionId={submissionId} />
        <MarkFilingCompleteAction />
      </Stack>
    );
  }

  // The Overview step is the page itself; don't list it as a card. Details is
  // surfaced inline below (a level above the per-section data entry), so it isn't
  // a section card either.
  const steps = (submission?.stepperSteps ?? []).filter(
    ({ step }) =>
      WASTE_SUBMISSION_STEP_PRESENTATION[step].routeSlug !== "" &&
      step !== SubmissionStepType.SubmissionDetails
  );

  return (
    <Stack spacing={2}>
      <StepHeader
        title="Submission Overview"
        description="Review and complete each section before submitting this 8700-12 notification."
      />
      <DetailsCard />
      <Stack spacing={1}>
        {steps.map(({ step }) => {
          const presentation = WASTE_SUBMISSION_STEP_PRESENTATION[step];
          return (
            <SectionCard
              key={step}
              step={step}
              status={stepStates[step]}
              issues={issuesByStep[step] ?? []}
              onOpen={() =>
                navigate(stepperStepPath(tenantId, submissionId, presentation.routeSlug))
              }
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
